import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, Loader2, Send, Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function Avatar({ user, size = 36 }) {
  return (
    <div className="flex items-center justify-center overflow-hidden rounded-full bg-accent font-bold text-white"
         style={{ width: size, height: size, minWidth: size, fontSize: size / 2.6 }}>
      {user?.avatar ? <img src={fileUrl(user.avatar)} alt="" className="h-full w-full object-cover" />
        : (user?.name?.[0] || "?").toUpperCase()}
    </div>
  );
}

function CommentRow({ c, onLike, onReply, onDelete, isReply }) {
  const { user } = useAuth();
  const mine = c.author.id === user.id;
  return (
    <div className={`flex gap-3 ${isReply ? "ml-11 mt-3" : "mt-4"}`} data-testid={`comment-${c.id}`}>
      <Avatar user={c.author} size={isReply ? 30 : 38} />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: "var(--text)" }}>{c.author.name}</span>
          {c.author.roles?.[0] && (
            <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold" style={{ background: "rgba(245,158,11,0.15)", color: "var(--accent)" }}>{c.author.roles[0]}</span>
          )}
          <span className="text-xs text-dim">{timeAgo(c.created_at)}</span>
        </div>
        <p className="mt-0.5 text-[15px] leading-snug" style={{ color: "var(--text)" }}>{c.text}</p>
        <div className="mt-1.5 flex items-center gap-4">
          {!isReply && (
            <button onClick={() => onReply(c)} data-testid={`reply-btn-${c.id}`} className="text-xs font-bold text-dim">Reply</button>
          )}
          {mine && (
            <button onClick={() => onDelete(c)} data-testid={`del-comment-${c.id}`} className="text-xs font-bold" style={{ color: "var(--red)" }}>
              <Trash2 size={13} className="inline" /> Delete
            </button>
          )}
        </div>
      </div>
      <button onClick={() => onLike(c)} data-testid={`like-comment-${c.id}`} className="pressable flex flex-col items-center gap-0.5 pt-1">
        <motion.span whileTap={{ scale: 1.4 }}>
          <Heart size={18} fill={c.liked ? "#F59E0B" : "transparent"} color={c.liked ? "#F59E0B" : "var(--text-dim)"} />
        </motion.span>
        <span className="text-[11px] font-semibold text-dim">{c.like_count || 0}</span>
      </button>
    </div>
  );
}

export default function CommentsSheet({ listingId, open, onClose, onCount }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["comments", listingId],
    queryFn: async () => {
      const d = (await api.get(`/listings/${listingId}/comments`)).data;
      onCount?.(d.count);
      return d;
    },
    enabled: open,
    refetchInterval: open ? 6000 : false,
  });

  const items = data?.items || [];

  const like = async (c) => {
    try {
      const { data: r } = await api.post(`/comments/${c.id}/like`);
      qc.setQueryData(["comments", listingId], (old) => {
        if (!old) return old;
        const upd = (x) => (x.id === c.id ? { ...x, liked: r.liked, like_count: r.like_count } : x);
        return { ...old, items: old.items.map((t) => ({ ...upd(t), replies: t.replies.map(upd) })) };
      });
    } catch { toast.error("Please log in to like"); }
  };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/listings/${listingId}/comments`, { text: text.trim(), parent_id: replyTo?.id || null });
      setText(""); setReplyTo(null);
      qc.invalidateQueries({ queryKey: ["comments", listingId] });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to comment"); }
    finally { setSending(false); }
  };

  const del = async (c) => {
    try {
      await api.delete(`/comments/${c.id}`);
      qc.invalidateQueries({ queryKey: ["comments", listingId] });
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-[75]" style={{ background: "rgba(0,0,0,0.5)" }}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div className="fixed bottom-0 left-1/2 z-[80] flex w-full flex-col goe-surface"
                      style={{ maxWidth: 520, height: "82vh", borderTopLeftRadius: 28, borderTopRightRadius: 28, borderTop: "1px solid var(--border)" }}
                      initial={{ y: "100%", x: "-50%" }} animate={{ y: 0, x: "-50%" }} exit={{ y: "100%", x: "-50%" }}
                      transition={{ type: "spring", stiffness: 320, damping: 32 }} data-testid="comments-sheet">
            <div className="mx-auto mt-3 h-1.5 w-12 rounded-full" style={{ background: "var(--border)" }} />
            <div className="flex items-center justify-between px-5 py-3">
              <h3 className="font-display text-lg font-extrabold">{data?.count || 0} Comments</h3>
              <button onClick={onClose} className="pressable glass flex h-9 w-9 items-center justify-center rounded-full"><X size={16} /></button>
            </div>

            <div className="no-scrollbar flex-1 overflow-y-auto px-5 pb-3">
              {isLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin text-dim" /></div>
              ) : items.length === 0 ? (
                <div className="py-16 text-center text-dim">
                  <p className="font-semibold" style={{ color: "var(--text)" }}>No comments yet</p>
                  <p className="mt-1 text-sm">Be the first to comment</p>
                </div>
              ) : (
                items.map((c) => (
                  <div key={c.id}>
                    <CommentRow c={c} onLike={like} onReply={setReplyTo} onDelete={del} />
                    {c.replies?.map((r) => (
                      <CommentRow key={r.id} c={r} onLike={like} onReply={setReplyTo} onDelete={del} isReply />
                    ))}
                  </div>
                ))
              )}
            </div>

            <div className="glass px-4 py-3" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
              {replyTo && (
                <div className="mb-2 flex items-center justify-between rounded-xl px-3 py-1.5 text-xs" style={{ background: "var(--surface-2)" }}>
                  <span className="text-dim">Replying to <b style={{ color: "var(--text)" }}>{replyTo.author.name}</b></span>
                  <button onClick={() => setReplyTo(null)}><X size={14} className="text-dim" /></button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Avatar user={user} size={34} />
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                       placeholder={replyTo ? "Write a reply…" : "Add a comment…"} data-testid="comment-input" maxLength={500}
                       className="glass flex-1 rounded-full px-4 py-2.5 text-[15px] outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
                <button onClick={send} disabled={sending || !text.trim()} data-testid="comment-send"
                        className="btn-accent flex h-10 w-10 items-center justify-center rounded-full disabled:opacity-50">
                  {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
